import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { getAccountType } from "../store/auth/selectors";
import { checkPermission } from "../shared/utils/permissions";
import { PERMISSION_TYPES } from "../shared/utils/permissions";
import MapInviteCodePopup from "../shared/components/Popup/MapInviteCodePopup";
import useInviteCodeQueries from "../store/InviteCodes/inviteCodeQueries";
import Pagination from "../shared/components/Pagination/Pagination";
import loader from "../assets/gifs/loader.gif";
import search_icon from "../assets/images/search_icon.svg";
import closeIcon from "../assets/images/close_icon.svg";
import "./InviteCodes.css";

const NotificationPortal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

const InviteCodes = () => {
  const accountType = useSelector(getAccountType);
  const checkUserPermission = (permissionType) =>
    checkPermission(accountType, permissionType);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("UNUSED");
  const [showNotification, setShowNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [notificationData, setNotificationData] = useState({
    code: "",
    tenant: "",
  });

  useEffect(() => {
    let timer;
    if (showNotification) {
      timer = setTimeout(() => {
        setShowNotification(false);
      }, 30000);
    }
    return () => clearTimeout(timer);
  }, [showNotification]);

  useEffect(() => {
    let timer;
    if (showErrorNotification) {
      timer = setTimeout(() => {
        setShowErrorNotification(false);
      }, 30000);
    }
    return () => clearTimeout(timer);
  }, [showErrorNotification]);

  const {
    inviteCodes,
    totalCount,
    currentPage,
    itemsPerPage,
    handlePageChange,
    handleLimitChange,
    handleSearchQueryChange,
    handleStatusChange,
    isLoading,
    error,
  } = useInviteCodeQueries(1, 10, statusFilter);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleSearchQueryChange(searchQuery);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const handleShowNotification = (code, tenant) => {
    setNotificationData({ code, tenant });
    setShowNotification(true);
  };

  const handleShowErrorNotification = () => {
    setShowErrorNotification(true);
  };

  const handleCloseNotification = () => {
    setShowNotification(false);
  };

  const handleCloseErrorNotification = () => {
    setShowErrorNotification(false);
  };

  const handleCreateInviteCode = () => {
    if (checkUserPermission(PERMISSION_TYPES.CREATE_INVITE_CODES)) {
      setIsPopupVisible(true);
      setShowNotification(false);
      setShowErrorNotification(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    handleStatusChange(value);
  };

  const canCreateInviteCodes = checkUserPermission(
    PERMISSION_TYPES.CREATE_INVITE_CODES
  );

  if (error) {
    return <div className="error-message">Error: {error.message}</div>;
  }

  return (
    <>
      <div className="invite-codes-mainCont">
        <div className="headerCont">
          <div className="titlecont">
            <h3>Invite Codes</h3>
            <p>Here are some of the updates.</p>
          </div>
          {canCreateInviteCodes && (
            <div className="buttonCont">
              <button onClick={handleCreateInviteCode} className="dark_btn">
                MAP INVITE CODE
              </button>
            </div>
          )}
        </div>

        <div className="filters-wrapper-invite">
          <div className="search-wrapper">
            <img src={search_icon} alt="Search" className="search-icon" />
            <input
              type="text"
              placeholder="Search by tenant, code or school"
              value={searchQuery}
              onChange={handleSearch}
              className="invite-codes-page-search-input"
            />
          </div>
          <span style={{ fontSize: "14px" }}>Status</span>
          <select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            className="status-select"
          >
            <option value="ALL">ALL</option>
            <option value="USED">USED</option>
            <option value="UNUSED">UNUSED</option>
          </select>
        </div>

        {isLoading ? (
          <div className="loaderCont">
            <img
              src={loader}
              alt="Loading spinner"
              style={{ width: "200px", height: "200px", margin: "auto" }}
            />
          </div>
        ) : (
          <div className="tableCont">
            <div className="table-header">
              <div className="items-shown">
                <span style={{ fontSize: "14px" }}>
                  Total count: {totalCount}
                </span>
                <Pagination
                  totalItems={totalCount}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  itemsPerPageOptions={[10, 30, 50, 100]}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleLimitChange}
                />
              </div>
            </div>

            {inviteCodes && inviteCodes.length > 0 ? (
              <div className="table-container-invite">
                <div className="table-wrapper">
                  <table className="invite-codes-table">
                    <thead>
                      <tr>
                        <th>TENANT NAME</th>
                        <th>INVITATION CODE</th>
                        <th>SCHOOL NAME</th>
                        <th>SCHOOL ADDRESS</th>
                        <th>STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inviteCodes.map((item, index) => (
                        <tr key={index}>
                          <td>{item.tenantName}</td>
                          <td>{item.invitationCode}</td>
                          <td>{item.schoolName}</td>
                          <td>{item.schoolAddress}</td>
                          <td>
                            <span
                              className={`status-badge ${
                                item.status === "USED" ? "used" : "unused"
                              }`}
                            >
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="error-message">No results found</div>
            )}
          </div>
        )}
      </div>

      <MapInviteCodePopup
        isVisible={isPopupVisible}
        onClose={() => setIsPopupVisible(false)}
        onSuccess={handleShowNotification}
        onError={handleShowErrorNotification}
      />

      {showNotification && (
        <NotificationPortal>
          <div
            style={{
              position: "fixed",
              bottom: "0",
              right: "0",
              backgroundColor: "#fff",
              padding: " 30px 40px 30px 15px",
              borderRadius: "8px 0 0 0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              borderLeft: "8px solid #008000",
              zIndex: 1000,
            }}
          >
            <button
              onClick={handleCloseNotification}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
              }}
            >
              <img
                src={closeIcon}
                alt="close button"
                style={{ width: "16px", height: "16px" }}
              />
            </button>
            <h3
              style={{
                color: "#008000",
                margin: "0 0 8px 0",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              Invite Code Mapped
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#273743",
                fontWeight: "400",
              }}
            >
              The code {notificationData.code} has been mapped to{" "}
              {notificationData.tenant}
            </p>
          </div>
        </NotificationPortal>
      )}

      {showErrorNotification && (
        <NotificationPortal>
          <div
            style={{
              position: "fixed",
              bottom: "0",
              right: "0",
              backgroundColor: "#fff",
              padding: " 30px 40px 30px 15px",
              borderRadius: "8px 0 0 0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              borderLeft: "8px solid #ff0000",
              zIndex: 1000,
            }}
          >
            <button
              onClick={handleCloseErrorNotification}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0",
              }}
            >
              <img
                src={closeIcon}
                alt="close button"
                style={{ width: "16px", height: "16px" }}
              />
            </button>
            <h3
              style={{
                color: "#ff0000",
                margin: "0 0 8px 0",
                fontSize: "20px",
                fontWeight: "600",
              }}
            >
              Error
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#273743",
                fontWeight: "400",
              }}
            >
              Event could not be mapped, please try again later
            </p>
          </div>
        </NotificationPortal>
      )}
    </>
  );
};

export default InviteCodes;
